/**
 * Tracking Engine
 * ───────────────
 * Builds immutable audit records (`UsageEvent`) for every execution and provides
 * favorites helpers (create/rename/pin/reorder). The store persists whatever
 * these return; nothing here touches storage directly.
 */

import type {
  Combination,
  Favorite,
  ParameterAssignment,
  UsageEvent,
} from "@/types";
import type { ExecutionContext } from "./combinationEngine";
import { uid } from "@/lib/utils/id";

/** Create the immutable audit row for one execution. */
export function buildUsageEvent(
  combination: Combination,
  assignments: ParameterAssignment[],
  ctx: ExecutionContext,
): UsageEvent {
  return {
    id: uid("evt"),
    combinationId: combination.id,
    signature: combination.signature,
    actionType: ctx.actionType,
    userName: ctx.userName,
    workbookName: ctx.workbookName,
    dashboardName: ctx.dashboardName,
    timestamp: ctx.now ? ctx.now() : new Date().toISOString(),
    assignments,
  };
}

export function createFavorite(
  combination: Combination,
  name: string,
  createdBy: string,
  order: number,
): Favorite {
  return {
    id: uid("fav"),
    combinationId: combination.id,
    name: name.trim() || combination.signature,
    pinned: false,
    createdAt: new Date().toISOString(),
    createdBy,
    order,
  };
}

export function renameFavorite(favorite: Favorite, name: string): Favorite {
  return { ...favorite, name: name.trim() || favorite.name };
}

export function togglePin(favorite: Favorite): Favorite {
  return { ...favorite, pinned: !favorite.pinned };
}

/** Pinned favorites first (by manual order), then the rest by recency of order. */
export function sortFavorites(favorites: Favorite[]): Favorite[] {
  return [...favorites].sort((a, b) => {
    if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
    return a.order - b.order;
  });
}
